import React, { useState, useEffect } from 'react';
import { LoadingSpinner, AlertTriangleIcon } from './icons';

interface ConfirmDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  resourceName: string;
  confirmText: string;
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({ isOpen, onClose, onConfirm, resourceName, confirmText }) => {
    const [inputValue, setInputValue] = useState('');
    const [isConfirming, setIsConfirming] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            // Reset state when modal opens
            setInputValue('');
            setIsConfirming(false);
            setError(null);
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }
    
    const handleConfirm = async () => {
        setIsConfirming(true);
        setError(null);
        try {
            await onConfirm();
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setIsConfirming(false);
        }
    };
    
    const isConfirmationMatched = inputValue === confirmText;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative mx-auto border w-full max-w-lg shadow-lg rounded-md bg-white">
                <div className="p-5">
                    <div className="flex items-start space-x-4">
                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                            <AlertTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg leading-6 font-bold text-gray-900">Confirm Deletion</h3>
                            <div className="mt-2">
                                <p className="text-sm text-gray-600">
                                    Are you sure you want to permanently delete <strong className="font-semibold text-gray-800">{resourceName}</strong>?
                                    This action is irreversible and will remove all associated data.
                                </p>
                                <p className="mt-4 text-sm text-gray-600">
                                    To confirm, please type "<strong className="font-semibold text-red-700">{confirmText}</strong>" in the box below.
                                </p>
                                <input
                                    type="text"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    className="mt-2 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm"
                                    aria-label={`Type ${confirmText} to confirm deletion`}
                                />
                            </div>
                        </div>
                    </div>
                     {error && <p className="mt-3 text-sm text-red-600 p-2 bg-red-50 rounded text-center">{error}</p>}
                </div>
                <div className="items-center px-4 py-3 border-t bg-gray-50 flex flex-wrap justify-end gap-3 rounded-b-md">
                     <button
                        type="button"
                        onClick={onClose}
                        disabled={isConfirming}
                        className="px-4 py-2 bg-gray-200 text-gray-800 text-base font-medium rounded-md w-auto shadow-sm hover:bg-gray-300 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={!isConfirmationMatched || isConfirming}
                        className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-auto shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-red-300 disabled:cursor-not-allowed flex items-center"
                    >
                        {isConfirming && <LoadingSpinner className="w-5 h-5 mr-2" />}
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmDeleteModal;
